import {
  ArchiveRestore,
  Boxes,
  ClipboardList,
  FileClock,
  Headphones,
  MessagesSquare,
  LayoutDashboard,
  LogOut,
  Menu,
  PackageCheck,
  FileUp,
  Plane,
  ShieldCheck,
  Truck,
  Users,
  ShoppingCart,
  BadgeDollarSign,
  Link,
  X,
} from "lucide-react";
import { useState, type ComponentType } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getEnv } from "../lib/env";
import { hasPermission, type AdminPermission } from "../lib/permissions";
import { Brand } from "./Brand";

type NavItem = {
  to: string;
  label: string;
  caption: string;
  permission?: AdminPermission;
  superOnly?: boolean;
  icon: ComponentType<{ size?: number; strokeWidth?: number }>;
};

const navItems: NavItem[] = [
  { to: "/", label: "Dashboard", caption: "ภาพรวมวันนี้", permission: "dashboard.view", icon: LayoutDashboard },
  { to: "/users", label: "Users", caption: "บัญชีผู้ใช้", permission: "users.view", icon: Users },
  { to: "/trips", label: "Trips", caption: "ทริปและวันเดินทาง", permission: "trips.view", icon: Plane },
  { to: "/purchases", label: "Buy for me", caption: "คำขอซื้อ", permission: "purchases.view", icon: ClipboardList },
  { to: "/procurement", label: "Procurement", caption: "คิว Quote และสั่งซื้อ", permission: "purchases.view", icon: ShoppingCart },
  { to: "/packages", label: "Packages", caption: "รับเข้าและตรวจพัสดุ", permission: "packages.view", icon: PackageCheck },
  { to: "/partner-imports", label: "3PL imports", caption: "ตรวจและ Apply ไฟล์รายวัน", permission: "packages.view", icon: FileUp },
  { to: "/deliveries", label: "Deliveries", caption: "ส่งมอบถึงที่พัก", permission: "deliveries.view", icon: Truck },
  { to: "/support", label: "Support", caption: "เคสและ SLA", permission: "support.view", icon: Headphones },
  { to: "/live-chat", label: "Live chat", caption: "ข้อความจาก Guest", permission: "support.view", icon: MessagesSquare },
  { to: "/rentals", label: "Rentals", caption: "เช่า ส่ง และคืน", permission: "rentals.view", icon: Boxes },
  { to: "/leave", label: "Leave", caption: "รับคืนและ second life", permission: "leave.view", icon: ArchiveRestore },
  { to: "/finance", label: "Finance", caption: "ยอดชำระและ Stripe refund", permission: "finance.view", icon: BadgeDollarSign },
  { to: "/payment-links", label: "Payment links", caption: "สร้างลิงก์และตรวจ Transaction", permission: "finance.view", icon: Link },
  { to: "/audit", label: "Audit log", caption: "ประวัติการจัดการ", permission: "audit.view", icon: FileClock },
  { to: "/admins", label: "Admin access", caption: "สิทธิ์ผู้ดูแล", superOnly: true, icon: ShieldCheck },
];

export function AppShell() {
  const { access, identity, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const visibleItems = navItems.filter((item) => item.superOnly ? access?.role === "SUPER_ADMIN" : !item.permission || hasPermission(access, item.permission));
  const frontendUrl = getEnv().frontendUrl;

  return (
    <div className="app-shell">
      <button className={`nav-scrim ${open ? "nav-scrim--open" : ""}`} type="button" onClick={() => setOpen(false)} aria-label="ปิดเมนู" />
      <aside className={`sidebar ${open ? "sidebar--open" : ""}`}>
        <div className="sidebar__top"><Brand /><button className="sidebar__close" type="button" onClick={() => setOpen(false)} aria-label="ปิดเมนู"><X size={19} /></button></div>
        <div className="pilot-stamp"><span>BKK</span><p><strong>Pre-arrival pilot</strong><small>Operations console · live data</small></p></div>
        <nav className="side-nav" aria-label="Admin navigation">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            return <NavLink key={item.to} to={item.to} end={item.to === "/"} onClick={() => setOpen(false)} className={({ isActive }) => `side-nav__item ${isActive ? "side-nav__item--active" : ""}`}><Icon size={18} strokeWidth={1.8} /><span><strong>{item.label}</strong><small>{item.caption}</small></span></NavLink>;
          })}
        </nav>
        <div className="sidebar__footer">
          <a className="traveler-link" href={frontendUrl} target="_blank" rel="noreferrer"><Plane size={16} />เปิดหน้า Traveler</a>
          <div className="identity-card"><span className="identity-card__avatar">{identity?.email?.slice(0, 1).toUpperCase() || "A"}</span><span><strong>{identity?.email || "Admin"}</strong><small>{access?.role === "SUPER_ADMIN" ? "Super Admin" : "Admin"}</small></span><button type="button" onClick={() => void logout()} aria-label="ออกจากระบบ"><LogOut size={17} /></button></div>
        </div>
      </aside>
      <div className="app-frame">
        <header className="topbar">
          <button className="menu-button" type="button" onClick={() => setOpen(true)} aria-label="เปิดเมนู"><Menu size={20} /></button>
          <div className="topbar__route"><span>CONTROL /</span><strong>{navItems.find((item) => item.to === location.pathname)?.label || "Operations"}</strong></div>
          <div className="system-live"><i />SYSTEM LIVE <span>ICT</span></div>
        </header>
        <main className="app-main"><Outlet /></main>
      </div>
    </div>
  );
}
